// iter78t — Freeform floor-plan editor.
// SVG canvas where the admin draws walls (rectangles, circles, polylines),
// drops locations as rotatable rectangles/circles, drags to position, and
// resizes/rotates with handle drags. Mode toggle:
//   • SELECT   — click+drag to move; corner handle resizes; top handle rotates.
//   • RECT     — drag a wall rectangle.
//   • CIRCLE   — drag a wall circle.
//   • PENCIL   — click points (double-click to finish) for polyline walls.
//   • PLACE    — click an empty spot to drop the currently picked palette location.
//
// Coordinates are stored in arbitrary "feet" units. The print view renders the
// canvas at fixed 0.5 in / unit so the printout matches the editor 1:1.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  MousePointer2, Square, Circle as CircleIcon, Pencil, MapPin,
  Save, Printer, RotateCw, Trash2, Tag,
} from 'lucide-react';

const BIN_TYPE_COLORS = {
  Receiving: { fill: '#dbeafe', stroke: '#2563eb', text: '#1e3a8a', label: 'RCV' },
  Storage: { fill: '#fef9c3', stroke: '#ca8a04', text: '#713f12', label: 'STK' },
  WIP: { fill: '#fed7aa', stroke: '#ea580c', text: '#7c2d12', label: 'WIP' },
  FinishedGoods: { fill: '#dcfce7', stroke: '#16a34a', text: '#14532d', label: 'FG' },
};
const tone = (binType) => BIN_TYPE_COLORS[binType] || { fill: '#f1f5f9', stroke: '#64748b', text: '#0f172a', label: '—' };

const MODES = [
  { id: 'select', label: 'Select', icon: MousePointer2, hint: 'Click to select. Drag body to move, corner to resize, top-bubble to rotate.' },
  { id: 'rect', label: 'Wall ▭', icon: Square, hint: 'Drag to draw a rectangle wall.' },
  { id: 'circle', label: 'Wall ◯', icon: CircleIcon, hint: 'Drag to draw a circular wall.' },
  { id: 'pencil', label: 'Wall ✎', icon: Pencil, hint: 'Click to add points. Double-click to finish polyline.' },
  { id: 'place', label: 'Place', icon: MapPin, hint: 'Pick a location in the palette, then click to drop it.' },
];

const newId = () => 'w_' + Math.random().toString(36).slice(2, 10);

const FreeformFloorPlan = ({
  api, locations, freeform, onPersist, onPrintSign, busy,
}) => {
  const svgRef = useRef(null);
  const [mode, setMode] = useState('select');
  const [width, setWidth] = useState(freeform?.width || 100);
  const [height, setHeight] = useState(freeform?.height || 60);
  const [title, setTitle] = useState(freeform?.title || 'Shop Floor');
  const [walls, setWalls] = useState(freeform?.walls || []);
  const [placements, setPlacements] = useState({}); // id -> {shape,x,y,w,h,rotation}
  const [selected, setSelected] = useState(null); // {kind:'wall'|'loc', id}
  const [pickedLocId, setPickedLocId] = useState(null);
  const [drawing, setDrawing] = useState(null); // {kind, start:{x,y}, current:{x,y}} for rect/circle
  const [polyDraft, setPolyDraft] = useState(null); // {points:[]}
  const [drag, setDrag] = useState(null); // {kind, id, mode:'move'|'resize'|'rotate', dx,dy, initial}
  const [dirty, setDirty] = useState(false);

  // Hydrate initial placements from saved location.freeform
  useEffect(() => {
    const map = {};
    (locations || []).forEach(l => {
      if (l.freeform && l.freeform.w && l.freeform.h) map[l.id] = { ...l.freeform };
    });
    setPlacements(map);
  }, [locations]);

  useEffect(() => {
    setWidth(freeform?.width || 100);
    setHeight(freeform?.height || 60);
    setTitle(freeform?.title || 'Shop Floor');
    setWalls(freeform?.walls || []);
  }, [freeform]);

  const markDirty = () => setDirty(true);

  // SVG → world coordinate conversion (handles viewBox/scaling)
  const toWorld = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    const m = svg.getScreenCTM().inverse();
    const w = pt.matrixTransform(m);
    return { x: w.x, y: w.y };
  };

  // Mouse handlers ---------------------------------------------------------
  const onMouseDown = (e) => {
    const pt = toWorld(e.clientX, e.clientY);
    if (mode === 'rect' || mode === 'circle') {
      setDrawing({ kind: mode, start: pt, current: pt });
    } else if (mode === 'pencil') {
      const next = polyDraft?.points ? [...polyDraft.points, pt] : [pt];
      setPolyDraft({ points: next });
    } else if (mode === 'place' && pickedLocId) {
      // Drop a 6×3 rectangle at the click point
      setPlacements(p => ({ ...p, [pickedLocId]: { shape: 'rect', x: pt.x - 3, y: pt.y - 1.5, w: 6, h: 3, rotation: 0 } }));
      setPickedLocId(null);
      setMode('select');
      setSelected({ kind: 'loc', id: pickedLocId });
      markDirty();
    } else if (mode === 'select') {
      // Hit-test — fall through to onLocClick/onWallClick handlers
      // Clicking the empty canvas deselects
      if (e.target === svgRef.current) setSelected(null);
    }
  };
  const onMouseMove = (e) => {
    if (drawing) {
      setDrawing(d => ({ ...d, current: toWorld(e.clientX, e.clientY) }));
    } else if (drag) {
      const pt = toWorld(e.clientX, e.clientY);
      if (drag.kind === 'loc') {
        setPlacements(p => {
          const old = p[drag.id]; if (!old) return p;
          const next = { ...old };
          if (drag.mode === 'move') {
            next.x = drag.initial.x + (pt.x - drag.startWorld.x);
            next.y = drag.initial.y + (pt.y - drag.startWorld.y);
          } else if (drag.mode === 'resize') {
            next.w = Math.max(1, drag.initial.w + (pt.x - drag.startWorld.x));
            next.h = Math.max(1, drag.initial.h + (pt.y - drag.startWorld.y));
          } else if (drag.mode === 'rotate') {
            const cx = old.x + old.w / 2; const cy = old.y + old.h / 2;
            next.rotation = Math.atan2(pt.y - cy, pt.x - cx) * 180 / Math.PI + 90;
          }
          return { ...p, [drag.id]: next };
        });
        markDirty();
      }
    }
  };
  const onMouseUp = (e) => {
    if (drawing) {
      const start = drawing.start; const end = drawing.current;
      const x = Math.min(start.x, end.x), y = Math.min(start.y, end.y);
      const w = Math.abs(end.x - start.x), h = Math.abs(end.y - start.y);
      if (w > 0.5 && h > 0.5) {
        const id = newId();
        const next = drawing.kind === 'rect'
          ? { id, kind: 'rect', x, y, w, h, stroke: '#0f172a', stroke_width: 2 }
          : { id, kind: 'circle', x, y, w, h, stroke: '#0f172a', stroke_width: 2 };
        setWalls(ws => [...ws, next]);
        markDirty();
      }
      setDrawing(null);
      setMode('select');
    } else if (drag) {
      setDrag(null);
    }
  };
  const onDoubleClick = () => {
    if (mode === 'pencil' && polyDraft?.points?.length >= 2) {
      const pts = polyDraft.points;
      setWalls(ws => [...ws, { id: newId(), kind: 'polyline', points: pts, stroke: '#0f172a', stroke_width: 2 }]);
      setPolyDraft(null);
      setMode('select');
      markDirty();
    }
  };
  const onCanvasContextMenu = (e) => {
    if (polyDraft) { e.preventDefault(); setPolyDraft(null); setMode('select'); }
  };

  // Save / clear / delete --------------------------------------------------
  const save = async () => {
    try {
      await api().put('/location-map/freeform', { width, height, title, walls });
      const payload = Object.entries(placements).map(([id, p]) => ({ id, ...p }));
      // include cleared placements by looking at locations that previously had freeform but now don't
      (locations || []).forEach(l => {
        if (l.freeform && !placements[l.id]) {
          payload.push({ id: l.id, shape: 'rect', x: 0, y: 0, w: 0, h: 0, rotation: 0 }); // clear sentinel
        }
      });
      if (payload.length) await api().put('/location-map/freeform-placements', payload);
      setDirty(false);
      if (onPersist) onPersist();
    } catch (e) {
      // Surfaced by parent toast
      throw e;
    }
  };

  const deleteSelected = () => {
    if (!selected) return;
    if (selected.kind === 'wall') {
      setWalls(ws => ws.filter(w => w.id !== selected.id));
    } else if (selected.kind === 'loc') {
      setPlacements(p => { const c = { ...p }; delete c[selected.id]; return c; });
    }
    setSelected(null);
    markDirty();
  };

  const rotateSelected = (delta) => {
    if (!selected || selected.kind !== 'loc') return;
    setPlacements(p => {
      const old = p[selected.id]; if (!old) return p;
      return { ...p, [selected.id]: { ...old, rotation: (old.rotation || 0) + delta } };
    });
    markDirty();
  };

  const printPlan = () => {
    const w = window.open('', '_blank', 'width=1200,height=900');
    if (!w) return;
    const PX = 12; // 12 SVG units = 1 inch on print → adjustable
    const pageW = (width * PX) / 96; // inches
    const pageH = (height * PX) / 96;
    const svgInner = svgRef.current ? svgRef.current.innerHTML : '';
    w.document.write(`<!doctype html><html><head><title>${title}</title>
      <style>
        @page { size: ${Math.max(8.5, pageW).toFixed(1)}in ${Math.max(11, pageH).toFixed(1)}in; margin: 0.4in; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; font-family: 'IBM Plex Sans', system-ui, sans-serif; }
        h1 { margin: 0 0 6px; font-size: 16pt; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #0f172a; padding-bottom: 4px; }
        .legend { display:flex; gap:14px; font-size:9pt; margin: 4px 0 8px; }
        .swatch { display:inline-block; width:12px; height:12px; border:1.5px solid; border-radius: 2px; margin-right: 4px; vertical-align: middle; }
        svg { width: 100%; height: auto; }
      </style></head><body>
      <h1>${title.replace(/[<>&]/g, '')}</h1>
      <div class="legend">
        ${Object.entries(BIN_TYPE_COLORS).map(([k, v]) => `<span><span class="swatch" style="background:${v.fill};border-color:${v.stroke}"></span>${k}</span>`).join('')}
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMinYMin meet">${svgInner}</svg>
      <script>window.onload=function(){window.focus();window.print();window.onafterprint=function(){window.close();};};</script>
      </body></html>`);
    w.document.close();
  };

  // Render -----------------------------------------------------------------
  const palette = (locations || []).filter(l => !placements[l.id]);
  const modeMeta = MODES.find(m => m.id === mode);

  return (
    <div className="space-y-3">
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            Freeform Floor Plan
            {dirty && <Badge variant="outline" className="text-orange-600 border-orange-300">Unsaved</Badge>}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {MODES.map(m => {
              const Icon = m.icon;
              return (
                <Button
                  key={m.id}
                  variant={mode === m.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setMode(m.id); setPolyDraft(null); setSelected(null); }}
                  className={`rounded-sm ${mode === m.id ? 'bg-slate-900' : ''}`}
                  data-testid={`ff-mode-${m.id}`}
                  title={m.hint}
                >
                  <Icon className="w-3.5 h-3.5 mr-1" /> {m.label}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" onClick={printPlan} className="rounded-sm" data-testid="ff-print-btn">
              <Printer className="w-3.5 h-3.5 mr-1" /> Print
            </Button>
            <Button size="sm" onClick={save} disabled={!dirty || busy} className="bg-orange-500 hover:bg-orange-600 rounded-sm" data-testid="ff-save-btn">
              <Save className="w-3.5 h-3.5 mr-1" /> Save
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={title} onChange={(e) => { setTitle(e.target.value); markDirty(); }} className="h-8 rounded-sm" data-testid="ff-title-input" />
            </div>
            <div>
              <Label className="text-xs">Canvas width (ft)</Label>
              <Input type="number" min={10} max={500} value={width} onChange={(e) => { setWidth(parseFloat(e.target.value) || 100); markDirty(); }} className="h-8 rounded-sm w-28 mono" data-testid="ff-width-input" />
            </div>
            <div>
              <Label className="text-xs">Canvas height (ft)</Label>
              <Input type="number" min={10} max={500} value={height} onChange={(e) => { setHeight(parseFloat(e.target.value) || 60); markDirty(); }} className="h-8 rounded-sm w-28 mono" data-testid="ff-height-input" />
            </div>
          </div>

          <div className="text-xs text-slate-500 flex items-center gap-2 bg-slate-50 rounded-sm p-2">
            <modeMeta.icon className="w-3.5 h-3.5 text-slate-600" /> {modeMeta.hint}
            {mode === 'place' && pickedLocId && <Badge variant="outline" className="ml-1 text-orange-600 border-orange-300">Picked</Badge>}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-3">
            <div className="border-2 border-slate-200 rounded-sm bg-slate-50 overflow-auto" style={{ minHeight: 500 }}>
              <svg
                ref={svgRef}
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="xMinYMin meet"
                style={{ width: '100%', height: 'auto', minHeight: 500, cursor: mode === 'pencil' ? 'crosshair' : mode === 'place' ? 'copy' : 'default', background: '#fff' }}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onDoubleClick={onDoubleClick}
                onContextMenu={onCanvasContextMenu}
                data-testid="ff-svg"
              >
                {/* grid */}
                <defs>
                  <pattern id="ff-grid" width="5" height="5" patternUnits="userSpaceOnUse">
                    <path d="M5 0 L0 0 0 5" fill="none" stroke="#e2e8f0" strokeWidth="0.1" />
                  </pattern>
                </defs>
                <rect x="0" y="0" width={width} height={height} fill="url(#ff-grid)" />

                {/* walls */}
                {walls.map(wall => {
                  const isSel = selected && selected.kind === 'wall' && selected.id === wall.id;
                  const sw = (wall.stroke_width || 2) * (isSel ? 1.5 : 1);
                  if (wall.kind === 'rect') {
                    return <rect key={wall.id} x={wall.x} y={wall.y} width={wall.w} height={wall.h} fill="none" stroke={isSel ? '#ea580c' : wall.stroke} strokeWidth={sw}
                      onClick={(e) => { if (mode === 'select') { e.stopPropagation(); setSelected({ kind: 'wall', id: wall.id }); } }} />;
                  }
                  if (wall.kind === 'circle') {
                    const cx = wall.x + wall.w / 2, cy = wall.y + wall.h / 2;
                    const rx = wall.w / 2, ry = wall.h / 2;
                    return <ellipse key={wall.id} cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke={isSel ? '#ea580c' : wall.stroke} strokeWidth={sw}
                      onClick={(e) => { if (mode === 'select') { e.stopPropagation(); setSelected({ kind: 'wall', id: wall.id }); } }} />;
                  }
                  if (wall.kind === 'polyline' && wall.points) {
                    return <polyline key={wall.id} points={wall.points.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke={isSel ? '#ea580c' : wall.stroke} strokeWidth={sw}
                      onClick={(e) => { if (mode === 'select') { e.stopPropagation(); setSelected({ kind: 'wall', id: wall.id }); } }} />;
                  }
                  return null;
                })}

                {/* in-progress draw preview */}
                {drawing && drawing.kind === 'rect' && (
                  <rect x={Math.min(drawing.start.x, drawing.current.x)} y={Math.min(drawing.start.y, drawing.current.y)}
                    width={Math.abs(drawing.current.x - drawing.start.x)} height={Math.abs(drawing.current.y - drawing.start.y)}
                    fill="rgba(234,88,12,0.08)" stroke="#ea580c" strokeWidth="0.3" strokeDasharray="0.5 0.4" />
                )}
                {drawing && drawing.kind === 'circle' && (() => {
                  const x = Math.min(drawing.start.x, drawing.current.x), y = Math.min(drawing.start.y, drawing.current.y);
                  const w = Math.abs(drawing.current.x - drawing.start.x), h = Math.abs(drawing.current.y - drawing.start.y);
                  return <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} fill="rgba(234,88,12,0.08)" stroke="#ea580c" strokeWidth="0.3" strokeDasharray="0.5 0.4" />;
                })()}
                {polyDraft && polyDraft.points?.length > 0 && (
                  <polyline points={polyDraft.points.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#ea580c" strokeWidth="0.3" strokeDasharray="0.5 0.4" />
                )}

                {/* placements (locations) */}
                {(locations || []).map(loc => {
                  const p = placements[loc.id];
                  if (!p) return null;
                  const t = tone(loc.bin_type);
                  const isSel = selected && selected.kind === 'loc' && selected.id === loc.id;
                  const cx = p.x + p.w / 2, cy = p.y + p.h / 2;
                  const transform = p.rotation ? `rotate(${p.rotation} ${cx} ${cy})` : undefined;
                  const fontSize = Math.max(1.6, Math.min(p.w, p.h) * 0.18);
                  return (
                    <g key={loc.id} transform={transform}
                      onMouseDown={(e) => {
                        if (mode !== 'select') return;
                        e.stopPropagation();
                        const w = toWorld(e.clientX, e.clientY);
                        setSelected({ kind: 'loc', id: loc.id });
                        setDrag({ kind: 'loc', id: loc.id, mode: 'move', startWorld: w, initial: { ...p } });
                      }}
                      style={{ cursor: 'move' }}
                      data-testid={`ff-loc-${loc.id}`}
                    >
                      {p.shape === 'circle' ? (
                        <ellipse cx={cx} cy={cy} rx={p.w / 2} ry={p.h / 2} fill={t.fill} stroke={isSel ? '#ea580c' : t.stroke} strokeWidth={isSel ? 0.4 : 0.25} />
                      ) : (
                        <rect x={p.x} y={p.y} width={p.w} height={p.h} fill={t.fill} stroke={isSel ? '#ea580c' : t.stroke} strokeWidth={isSel ? 0.4 : 0.25} />
                      )}
                      <text x={cx} y={cy - fontSize * 0.4} textAnchor="middle" fontSize={fontSize} fontWeight="700" fill={t.text}>{(loc.location_name || '').slice(0, 18)}</text>
                      <text x={cx} y={cy + fontSize * 0.9} textAnchor="middle" fontSize={fontSize * 0.85} fontFamily="monospace" fill={t.text}>{loc.bin_id}</text>
                      {isSel && mode === 'select' && (
                        <>
                          {/* resize handle: bottom-right corner */}
                          <rect
                            x={p.x + p.w - 0.6} y={p.y + p.h - 0.6} width="1.2" height="1.2"
                            fill="#0f172a" stroke="#fff" strokeWidth="0.15" style={{ cursor: 'se-resize' }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              const w = toWorld(e.clientX, e.clientY);
                              setDrag({ kind: 'loc', id: loc.id, mode: 'resize', startWorld: w, initial: { ...p } });
                            }}
                          />
                          {/* rotation handle: above top center */}
                          <line x1={cx} y1={p.y} x2={cx} y2={p.y - 2.5} stroke="#0f172a" strokeWidth="0.15" />
                          <circle
                            cx={cx} cy={p.y - 2.5} r="0.9"
                            fill="#ea580c" stroke="#fff" strokeWidth="0.15" style={{ cursor: 'grab' }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              const w = toWorld(e.clientX, e.clientY);
                              setDrag({ kind: 'loc', id: loc.id, mode: 'rotate', startWorld: w, initial: { ...p } });
                            }}
                          />
                        </>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>

            {/* Side panel */}
            <div className="space-y-2">
              {selected && (
                <Card className="industrial-card">
                  <CardContent className="p-3 space-y-2 text-xs">
                    <p className="font-semibold text-slate-700">Selection</p>
                    <p className="text-slate-500">{selected.kind === 'wall' ? 'Wall element' : 'Location placement'}</p>
                    {selected.kind === 'loc' && (
                      <div className="flex flex-col gap-1">
                        <Button size="sm" variant="outline" onClick={() => rotateSelected(-15)} className="rounded-sm" data-testid="ff-rotate-left"><RotateCw className="w-3 h-3 mr-1 -scale-x-100" /> -15°</Button>
                        <Button size="sm" variant="outline" onClick={() => rotateSelected(15)} className="rounded-sm" data-testid="ff-rotate-right"><RotateCw className="w-3 h-3 mr-1" /> +15°</Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          setPlacements(p => { const old = p[selected.id]; return { ...p, [selected.id]: { ...old, shape: old.shape === 'rect' ? 'circle' : 'rect' } }; });
                          markDirty();
                        }} className="rounded-sm" data-testid="ff-toggle-shape">Toggle ▭/◯</Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          const loc = (locations || []).find(l => l.id === selected.id);
                          if (loc && onPrintSign) onPrintSign(loc);
                        }} className="rounded-sm" data-testid="ff-print-sign"><Tag className="w-3 h-3 mr-1" /> Print Sign</Button>
                      </div>
                    )}
                    <Button size="sm" variant="outline" onClick={deleteSelected} className="text-red-600 hover:bg-red-50 rounded-sm w-full" data-testid="ff-delete-selected">
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </CardContent>
                </Card>
              )}

              <Card className="industrial-card">
                <CardHeader className="pb-2"><CardTitle className="text-xs flex items-center justify-between"><span>Palette ({palette.length})</span></CardTitle></CardHeader>
                <CardContent className="p-2 max-h-80 overflow-y-auto space-y-1.5">
                  {palette.length === 0 && <p className="text-xs text-slate-400 text-center py-4">All locations on canvas.</p>}
                  {palette.map(loc => {
                    const t = tone(loc.bin_type);
                    const isPicked = pickedLocId === loc.id;
                    return (
                      <button
                        key={loc.id}
                        type="button"
                        onClick={() => { setPickedLocId(loc.id); setMode('place'); }}
                        className={`w-full text-left rounded-sm p-2 border-2 transition ${isPicked ? 'ring-2 ring-orange-500' : 'hover:shadow'}`}
                        style={{ background: t.fill, borderColor: t.stroke, color: t.text }}
                        data-testid={`ff-palette-${loc.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[9px] font-bold tracking-wider px-1 rounded" style={{ background: t.stroke, color: 'white' }}>{t.label}</span>
                          {isPicked && <span className="text-[9px] uppercase">Picked</span>}
                        </div>
                        <div className="font-semibold text-xs mt-1 leading-tight line-clamp-2">{loc.location_name}</div>
                        <div className="font-mono text-[10px] opacity-80">{loc.bin_id}</div>
                      </button>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FreeformFloorPlan;
